// PRD-09 shared avatar helpers. The avatar renders one of three ways (avatar_type):
// an uploaded photo, the org logo, or initials on a brand-palette colour. Glyph/initials
// are derived here (never stored). Kept framework-free so both the Edit Profile modal and
// the header avatar resolve identically.

// Official RHD brand palette (RHD Logo Guidelines, 27 Mar 2026). Mirrors the server
// allow-list in resapi user_profile_routes._AVATAR_PALETTE — keep the two in sync.
export const AVATAR_PALETTE = [
  { hex: '#135eff', name: 'Primary Bright Blue' },
  { hex: '#233146', name: 'Primary Dark Blue' },
  { hex: '#00deff', name: 'Icon Light Blue' },
  { hex: '#1fbcbc', name: 'Icon Green' },
  { hex: '#f0a500', name: 'Icon Gold' },
  { hex: '#d9e8e6', name: 'Icon Grey' },
  { hex: '#008eff', name: 'WCAG Bright Blue' },
];

export function initialsOf(name) {
  if (!name || typeof name !== 'string') return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Stable palette pick from a name, so an un-chosen avatar is still consistent per user.
export function autoColor(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) & 0xffffffff;
  }
  const idx = Math.abs(h) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx].hex;
}

// Legible text colour for a given fill (dark text on light swatches, white otherwise).
export function textOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return '#ffffff';
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  // Relative luminance (sRGB approximation).
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#233146' : '#ffffff';
}

// Resolve the initials-fallback style {background, color} for a name + chosen colour.
export function initialsStyle(name, chosenColor) {
  const color = chosenColor || autoColor(name);
  return { background: color, color: textOn(color) };
}

// Single source of truth for which source an avatar renders: 'photo' | 'logo' | 'color'.
// avatar_type is the selector; when unset (legacy) we prefer an existing photo, else
// fall back to coloured initials. 'photo'/'logo' degrade to 'color' if the asset is gone.
export function resolveAvatarKind(profile, orgHasLogo) {
  const type = profile?.avatar_type;
  if (type === 'photo' && profile?.photo_url) return 'photo';
  if (type === 'logo' && orgHasLogo) return 'logo';
  if (type === 'color') return 'color';
  if (!type && profile?.photo_url) return 'photo';
  return 'color';
}
